/**
 * What the renderer tells the ranger's rig beyond where he is and which way he faces (ROUND7 §3): his look -
 * with the ghillie wrap when `Ranger.gear.ghillie` says he carries one - while `Ranger.sneaking`, the crouched
 * counterpart of the upright pose `poseRanger` chose from the rest of his state, and which way a stab goes.
 */

import type { SoldierLook } from './model';
import { flinchAt, kickAt } from './poseActions';
import type { Motion } from './poseBase';

/** The player's ranger, wrapped in scrim or not. */
export function rangerLook(ghillie: boolean): SoldierLook {
  const look: SoldierLook = { side: 'ranger', skin: 'skinB', headgear: 'helmet', pack: true };
  return ghillie ? { ...look, ghillie: true } : look;
}

/**
 * In a stolen uniform (02 §6.4): same man, their helmet band, no pack - and no amber scarf to give him away.
 * No ghillie wrap either: a man in their uniform draped in scrim would be a man to shoot.
 */
export const DISGUISE_LOOK: SoldierLook = { side: 'enemy', skin: 'skinB', headgear: 'helmet', pack: false };

/** The part of `SoldierState` a crouch rewrites. */
export interface Crouchable {
  motion: Motion;
  age: number;
  kick: number;
  flinch: number;
}

/**
 * Turn the upright pose a sneaking man was given into its crouched counterpart: the walk into the crouched
 * walk, standing still into the crouch, the raised rifle (and firing it) into the crouched aim, the stagger
 * into a crouch that reels. The rest - the throw, the lunge, the crawl, the fall - stand as chosen: the sim
 * only lets him sneak on his feet and not running, and those are things he stands up, or goes down, to do.
 */
export function crouchFor(s: Crouchable, sneaking: boolean): void {
  if (!sneaking) return;
  switch (s.motion) {
    case 'walk':
      s.motion = 'sneak';
      return;
    case 'idle':
      s.motion = 'crouch';
      return;
    case 'aim':
      s.motion = 'crouchAim';
      return;
    case 'fire':
      // Standing, the braced stance works its recoil out from `age`; crouched it is the overlay.
      s.kick = kickAt(s.age);
      s.motion = 'crouchAim';
      return;
    case 'hit':
      s.flinch = flinchAt(s.age);
      s.motion = 'crouch';
      return;
    default:
  }
}

/**
 * Which way a stab goes: at the point of its `knife` event, which the sim puts 0.8 m toward the man the blade
 * found - at point-blank that can be well off the aim - and along the aim when it found nobody. Standing on
 * the point itself (nothing to go by), the aim.
 */
export function stabBearing(ranger: { x: number; z: number; aim: number }, at: { x: number; z: number }): number {
  const dx = at.x - ranger.x;
  const dz = at.z - ranger.z;
  return dx * dx + dz * dz > 1e-6 ? Math.atan2(dx, dz) : ranger.aim;
}

/** Turn a lunge - and only a lunge - to its bearing, the whole man: legs, chest and knife hand go at him. */
export function lungeAt(s: { motion: Motion; facing: number; twist: number }, bearing: number | null): void {
  if (s.motion !== 'stab' || bearing === null) return;
  s.facing = bearing;
  s.twist = 0;
}
